import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatoMXN, formatoFecha, ESTADOS, PRIORIDADES } from '@/lib/api';
import { Printer, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrdenImprimir() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orden, setOrden] = useState(null);
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const [oRes, cRes] = await Promise.all([
          api.get(`/ordenes/${id}`),
          api.get('/configuracion')
        ]);
        setOrden(oRes.data);
        setConfig(cRes.data);
      } catch {
        navigate('/ordenes');
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [id, navigate]);

  if (cargando) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!orden || !config) return null;

  const lineStyle = { height: '22px', width: '100%' };

  return (
    <div className="min-h-screen bg-white" data-testid="orden-imprimir-page">
      {/* Barra de acciones - se oculta al imprimir */}
      <div className="print-hide sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/ordenes/${id}`)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="volver-orden-btn">
          <ArrowLeft className="w-4 h-4" /> Volver a la orden
        </button>
        <button onClick={() => window.print()} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-sm text-sm font-bold transition-colors" data-testid="imprimir-orden-btn">
          <Printer className="w-4 h-4" /> Imprimir
        </button>
      </div>

      {/* Contenido imprimible - ajustado a 1 hoja */}
      <div className="print-page max-w-[800px] mx-auto px-8 py-5 print-content" style={{ fontFamily: 'Inter, sans-serif', color: '#0f172a' }}>

        {/* Encabezado compacto */}
        <div className="flex items-center justify-between border-b-2 border-slate-900 pb-2 mb-3">
          <div className="flex items-center gap-2">
            {config.logo_url ? (
              <img src={`${API_URL}${config.logo_url}?v=${encodeURIComponent(config.logo_actualizado_en || '')}`} alt="Logo" className="w-16 h-16 object-contain" data-testid="print-logo" />
            ) : (
              <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
              </div>
            )}
            <div>
              <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '20px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.1 }}>
                {config.nombre_taller}
              </h1>
              <p style={{ fontSize: '9px', color: '#64748b', marginTop: '2px' }}>{config.direccion}</p>
              <p style={{ fontSize: '9px', color: '#64748b' }}>
                Tel: {config.telefono} | {config.email}
                {config.rfc && <> | RFC: <span style={{ fontFamily: 'monospace' }}>{config.rfc}</span></>}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '22px', fontWeight: 700, color: '#f59e0b', lineHeight: 1 }}>
              {orden.numero_orden}
            </p>
            <p style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Orden de Trabajo</p>
          </div>
        </div>

        {/* Datos del cliente + Info orden en una sola fila compacta */}
        <div className="grid grid-cols-2 gap-4 mb-3" style={{ fontSize: '10px' }}>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '2px', padding: '6px 8px' }}>
            <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600, marginBottom: '3px' }}>Cliente</p>
            <p style={{ fontWeight: 600, fontSize: '11px' }}>{orden.cliente_nombre}</p>
            {orden.vehiculo_info && <p style={{ color: '#64748b', marginTop: '1px' }}>Vehículo: {orden.vehiculo_info}</p>}
            {orden.vehiculo?.color && <p style={{ color: '#64748b' }}>Color: {orden.vehiculo.color}</p>}
            {orden.vehiculo?.vin && <p style={{ color: '#64748b', fontFamily: 'monospace' }}>VIN: {orden.vehiculo.vin}</p>}
          </div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '2px', padding: '6px 8px' }}>
            <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600, marginBottom: '3px' }}>Información de la Orden</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 8px' }}>
              <span style={{ color: '#64748b' }}>Estado:</span>
              <span style={{ fontWeight: 600 }}>{ESTADOS[orden.estado]?.label}</span>
              <span style={{ color: '#64748b' }}>Prioridad:</span>
              <span style={{ fontWeight: 600 }}>{PRIORIDADES[orden.prioridad]?.label}</span>
              <span style={{ color: '#64748b' }}>Ingreso:</span>
              <span>{formatoFecha(orden.fecha_ingreso || orden.created_at)}</span>
              {orden.fecha_promesa && <>
                <span style={{ color: '#64748b' }}>Promesa:</span>
                <span>{formatoFecha(orden.fecha_promesa)}</span>
              </>}
              <span style={{ color: '#64748b' }}>Mecánico:</span>
              <span>{orden.mecanico_nombre || '-'}</span>
            </div>
          </div>
        </div>

        {/* Problema reportado - compacto */}
        {orden.problema_reportado && (
          <div className="mb-3" style={{ border: '1px solid #e2e8f0', borderRadius: '2px', padding: '6px 8px' }}>
            <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600, marginBottom: '2px' }}>Problema Reportado</p>
            <p style={{ fontSize: '10px', color: '#334155' }}>{orden.problema_reportado}</p>
          </div>
        )}

        {/* Diagnostico - compacto */}
        {orden.diagnostico && (
          <div className="mb-3" style={{ border: '1px solid #e2e8f0', borderRadius: '2px', padding: '6px 8px' }}>
            <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600, marginBottom: '2px' }}>Diagnóstico</p>
            <p style={{ fontSize: '10px', color: '#334155' }}>{orden.diagnostico}</p>
          </div>
        )}

        {/* SECCION PRINCIPAL: Trabajo Realizado - con lineas para escritura */}
        <div className="mb-3">
          <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b', fontWeight: 700, borderBottom: '2px solid #0f172a', paddingBottom: '3px', marginBottom: '6px' }}>
            Trabajo Realizado
          </p>
          {orden.reparacion_realizada ? (
            <p style={{ fontSize: '10px', color: '#334155', lineHeight: 1.5 }}>{orden.reparacion_realizada}</p>
          ) : (
            <p style={{ fontSize: '9px', color: '#94a3b8', fontStyle: 'italic' }}>Sin descripción registrada</p>
          )}
        </div>

        {/* Desglose de Refacciones */}
        {orden.partes?.length > 0 && (
          <div className="mb-3">
            <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b', fontWeight: 700, borderBottom: '2px solid #0f172a', paddingBottom: '3px', marginBottom: '4px' }}>
              Refacciones e Insumos
            </p>
            <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '3px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.05em' }}>Descripción</th>
                  <th style={{ textAlign: 'center', padding: '3px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8', width: '50px' }}>Cant.</th>
                  <th style={{ textAlign: 'right', padding: '3px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8', width: '80px' }}>P. Unit.</th>
                  <th style={{ textAlign: 'right', padding: '3px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8', width: '80px' }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {orden.partes.map((p, i) => (
                  <tr key={p.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '2px 0', color: '#334155' }}>
                      {p.sku && <span style={{ fontFamily: 'monospace', fontSize: '8px', color: '#94a3b8', marginRight: '4px' }}>[{p.sku}]</span>}
                      {p.descripcion}
                    </td>
                    <td style={{ padding: '2px 0', color: '#475569', textAlign: 'center' }}>{p.cantidad}</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontFamily: 'monospace', color: '#475569' }}>{formatoMXN(p.precio_unitario)}</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatoMXN(p.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid #cbd5e1' }}>
                  <td colSpan="3" style={{ padding: '3px 0', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Total Refacciones:</td>
                  <td style={{ padding: '3px 0', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{formatoMXN(orden.costo_partes)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Costos + Pagos lado a lado */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          {/* Desglose de costos */}
          <div>
            <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b', fontWeight: 700, borderBottom: '2px solid #0f172a', paddingBottom: '3px', marginBottom: '4px' }}>
              Resumen de Costos
            </p>
            <div style={{ fontSize: '10px' }}>
              <div className="flex justify-between" style={{ padding: '2px 0' }}>
                <span style={{ color: '#475569' }}>Mano de obra</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatoMXN(orden.costo_mano_obra)}</span>
              </div>
              <div className="flex justify-between" style={{ padding: '2px 0' }}>
                <span style={{ color: '#475569' }}>Refacciones ({orden.partes?.length || 0} pzas)</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatoMXN(orden.costo_partes)}</span>
              </div>
              <div className="flex justify-between" style={{ padding: '2px 0', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ color: '#475569' }}>Subtotal</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatoMXN(orden.subtotal)}</span>
              </div>
              {orden.descuento > 0 && (
                <div className="flex justify-between" style={{ padding: '2px 0' }}>
                  <span style={{ color: '#475569' }}>Descuento</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#15803d' }}>-{formatoMXN(orden.descuento)}</span>
                </div>
              )}
              {orden.incluir_iva && orden.iva > 0 && (
                <div className="flex justify-between" style={{ padding: '2px 0' }}>
                  <span style={{ color: '#475569' }}>IVA 16%</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatoMXN(orden.iva)}</span>
                </div>
              )}
              <div className="flex justify-between" style={{ padding: '4px 0', borderTop: '2px solid #0f172a', fontWeight: 700, fontSize: '12px' }}>
                <span>TOTAL</span>
                <span style={{ fontFamily: 'monospace' }}>{formatoMXN(orden.total)}</span>
              </div>
              <div className="flex justify-between" style={{ padding: '2px 0', fontSize: '10px' }}>
                <span style={{ color: '#475569' }}>Pagado</span>
                <span style={{ fontFamily: 'monospace', color: '#15803d' }}>{formatoMXN(orden.total_pagado)}</span>
              </div>
              <div className="flex justify-between" style={{ padding: '3px 0', borderTop: '1px solid #cbd5e1', fontWeight: 700, fontSize: '11px' }}>
                <span style={{ color: orden.saldo_pendiente > 0 ? '#b91c1c' : '#15803d' }}>Saldo</span>
                <span style={{ fontFamily: 'monospace', color: orden.saldo_pendiente > 0 ? '#b91c1c' : '#15803d' }}>{formatoMXN(orden.saldo_pendiente)}</span>
              </div>
            </div>
          </div>

          {/* Historial de pagos */}
          <div>
            <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b', fontWeight: 700, borderBottom: '2px solid #0f172a', paddingBottom: '3px', marginBottom: '4px' }}>
              Pagos Registrados
            </p>
            {orden.pagos?.length > 0 ? (
              <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '2px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8' }}>Fecha</th>
                    <th style={{ textAlign: 'left', padding: '2px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8' }}>Método</th>
                    <th style={{ textAlign: 'right', padding: '2px 0', fontSize: '8px', textTransform: 'uppercase', color: '#94a3b8' }}>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {orden.pagos.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '2px 0', color: '#475569' }}>{formatoFecha(p.fecha_pago)}</td>
                      <td style={{ padding: '2px 0', color: '#475569', textTransform: 'capitalize' }}>{p.metodo_pago}</td>
                      <td style={{ padding: '2px 0', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatoMXN(p.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ fontSize: '9px', color: '#94a3b8', textAlign: 'center', padding: '8px 0' }}>Sin pagos registrados</p>
            )}
          </div>
        </div>

        {/* Notas para el cliente */}
        {orden.notas_cliente && (
          <div className="mb-3" style={{ border: '1px solid #e2e8f0', borderRadius: '2px', padding: '5px 8px' }}>
            <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600, marginBottom: '2px' }}>Notas para el Cliente</p>
            <p style={{ fontSize: '10px', color: '#334155' }}>{orden.notas_cliente}</p>
          </div>
        )}

        {/* Firmas compactas */}
        <div className="grid grid-cols-2 gap-8 mt-4">
          <div className="text-center">
            <div style={{ borderTop: '1px solid #94a3b8', paddingTop: '4px', marginTop: '40px' }}>
              <p style={{ fontSize: '9px', color: '#64748b' }}>Firma del Taller</p>
              <p style={{ fontSize: '8px', color: '#94a3b8' }}>{config.nombre_taller}</p>
            </div>
          </div>
          <div className="text-center">
            <div style={{ borderTop: '1px solid #94a3b8', paddingTop: '4px', marginTop: '40px' }}>
              <p style={{ fontSize: '9px', color: '#64748b' }}>Firma del Cliente</p>
              <p style={{ fontSize: '8px', color: '#94a3b8' }}>{orden.cliente_nombre}</p>
            </div>
          </div>
        </div>

        {/* Pie compacto */}
        <div style={{ marginTop: '8px', paddingTop: '4px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          <p style={{ fontSize: '8px', color: '#94a3b8' }}>
            {config.nombre_taller} | {config.direccion} | Tel: {config.telefono}
          </p>
          <p style={{ fontSize: '8px', color: '#94a3b8', marginTop: '1px' }}>
            Documento generado el {new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
}
